
import { useState, useEffect } from "react";
import { Product, Group } from "@/lib/types";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import GroupProductSelector from "./GroupProductSelector";
import GroupProductQuantities, { ProductQuantity } from "./GroupProductQuantities";

export interface GroupFormData {
  name: string;
  description: string;
  productIds: string[];
  productQuantities?: ProductQuantity[];
}

interface GroupFormContentProps {
  products: Product[];
  initialData?: Partial<Group>;
  initialProductQuantities?: ProductQuantity[];
  onChange: (data: GroupFormData) => void;
}

const GroupFormContent = ({ 
  products, 
  initialData, 
  initialProductQuantities = [],
  onChange 
}: GroupFormContentProps) => {
  const [name, setName] = useState(initialData?.name || "");
  const [description, setDescription] = useState(initialData?.description || "");
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>(
    initialData?.productIds || []
  );
  const [productQuantities, setProductQuantities] = useState<ProductQuantity[]>(
    initialProductQuantities
  );
  
  // When initialProductQuantities changes, update our local state
  useEffect(() => {
    if (initialProductQuantities.length > 0) {
      console.log("Setting product quantities from prop:", initialProductQuantities);
      setProductQuantities(initialProductQuantities);
    }
  }, [initialProductQuantities]);
  
  // Notify parent of all changes
  useEffect(() => {
    console.log("Notifying parent of form data change:", {
      name,
      description,
      productIds: selectedProductIds,
      productQuantities
    });
    
    onChange({
      name,
      description,
      productIds: selectedProductIds,
      productQuantities,
    });
  }, [name, description, selectedProductIds, productQuantities, onChange]);
  
  // Handler for product quantity changes
  const handleQuantitiesChange = (quantities: ProductQuantity[]) => {
    console.log("Product quantities changed in form:", quantities);
    setProductQuantities(quantities);
  };
  
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="name">Group Name</Label>
        <Input
          id="name"
          placeholder="Enter group name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="description">Description (Optional)</Label>
        <Textarea
          id="description"
          placeholder="Enter group description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>
      
      <div className="space-y-2">
        <GroupProductSelector
          availableProducts={products}
          selectedProducts={selectedProductIds}
          onProductsChange={setSelectedProductIds}
        />
      </div>

      <GroupProductQuantities
        products={products}
        selectedProductIds={selectedProductIds}
        initialQuantities={productQuantities}
        onChange={handleQuantitiesChange}
      />
    </div>
  );
};

export default GroupFormContent;
